<script setup lang="ts">
import { MetalPlanData } from '@/page/domain/MetalPlanData';
import ShowPriceRate from '@/page/infrastructure/components/metalPlans/ShowPriceRate.vue';
import { StarsRate, useTranslation } from '@hfc/ui-core';
import { useUrlHelper } from '@/page/infrastructure/composition/useUrlHelper';
import MetalTierCardDetails from '@/page/infrastructure/components/metalPlans/MetalTierCardDetails.vue';
import MetalTierCardBoxDetails from '@/page/infrastructure/components/metalPlans/MetalTierCardBoxDetails.vue';
import { twMerge } from 'tailwind-merge';

const { getCdnUrl } = useUrlHelper();
const { t } = useTranslation();

const props = withDefaults(defineProps<{
  plan: MetalPlanData,
  highlight?: boolean,
  condensed?: boolean,
}>(), {
  condensed: false,
});

const baseClass = twMerge(
  'flex flex-col w-full border border-slate-50 rounded-md px-6 py-8 text-sm sm:text-base bg-white space-y-6',
  props.highlight && 'bg-sky-50',
  props.condensed && 'bg-transparent border-0',
);
</script>
<template>
  <div :class="baseClass">
    <div class="flex space-x-6" :class="{ 'flex-row': !condensed, 'flex-col items-center space-y-6': condensed }">
      <div class="flex w-28">
        <img :src="getCdnUrl(plan.icon)" :alt="plan.key" />
      </div>
      <div class="flex flex-col flex-1">
        <div class="flex flex-row mb-3">
          <h4 class="text-black">
            {{ plan.tierName }} {{ plan.tierNumber }}
            <span v-if="plan.tierSuffix" class="text-black">{{ plan.tierSuffix }}</span>
          </h4>
        </div>
        <div class="flex flex-row mb-2">
          {{ t('metalPlans.price') }}:&nbsp;&nbsp;<ShowPriceRate v-if="plan.priceRating" :price-rate="plan.priceRating" />
        </div>
        <div class="flex flex-row">
          {{ t('metalPlans.popularity') }}:&nbsp;&nbsp;<StarsRate v-if="plan.popularityRating" :popularity-rate="plan.popularityRating" icon-size="text-md"/>
        </div>
      </div>
    </div>
    <MetalTierCardDetails v-if="!condensed" :plan="plan" />
    <slot v-if="plan.showMedicalCosts && !condensed" name="box-details">
      <MetalTierCardBoxDetails :company-pays="plan.medicalCosts?.companyPays ?? `${plan.coverage}%`" :you-pay="plan.medicalCosts?.youPay ?? `${100 - plan.coverage}%`" :notes="plan.medicalCosts?.notes" />
    </slot>
  </div>
</template>
